// ============================================================================
// Separation module — the shapes, and the words on screen.
//
// ⛔ THE PERSONAL VIEW IS A FILTER, NOT A DIFFERENT DOCUMENT. Every label,
// sub-step and due date below is written ONCE and rendered in both the
// consolidated view and "My items". Nothing is reworded per audience — if the
// two ever say different things about the same item, one of them is wrong and
// nobody can tell which.
// ============================================================================

export const SEPARATION_TYPES = [
  'Resigned',
  'Terminated_For_Cause',
  'Laid_Off',
  'End_Of_Temp_Assignment',
  'Job_Abandonment',
  'Client_Conversion',
  'Deceased',
] as const;

export type SeparationType = (typeof SEPARATION_TYPES)[number];

/** A kind from the link, only if it is one of the seven. */
export function linkedType(v: string | null): SeparationType | '' {
  return v && (SEPARATION_TYPES as readonly string[]).includes(v) ? (v as SeparationType) : '';
}
/** A day from the link, only if it is a YYYY-MM-DD date. */
export function linkedDay(v: string | null): string {
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
}

export interface SeparationReasonSpec {
  value: SeparationType;
  label: string;
  blurb: string;
  /** Access is cut on submit rather than on the last working day. */
  immediate: boolean;
  rehireDefault: 'Eligible' | 'Not eligible' | 'n/a';
  /** Severance: on by default, available, or hidden behind a COO note. */
  severance: 'default' | 'available' | 'expected' | 'discouraged' | 'none';
  ceoAttestationRequired: boolean;
  exitInterview: boolean;
}

// The seven, and what each one actually drives. There is deliberately no
// "Retired" (TCS has no pension, and an unreachable option is one somebody
// eventually mis-selects) and no plain "Separated" — that is the name of the
// whole event, so using it as the reason answers nothing.
export const SEPARATION_REASONS: ReadonlyArray<SeparationReasonSpec> = [
  {
    value: 'Resigned',
    label: 'Resigned',
    blurb:
      'The employee chose to leave and gave notice. Access ends on the last working day. Exit interview expected.',
    immediate: false,
    rehireDefault: 'Eligible',
    severance: 'available',
    ceoAttestationRequired: false,
    exitInterview: true,
  },
  {
    value: 'Terminated_For_Cause',
    label: 'Terminated for cause',
    blurb:
      'TCS ended it for performance or conduct. Access is cut on submit. Requires an answer on the performance trail and a CEO attestation.',
    immediate: true,
    rehireDefault: 'Not eligible',
    severance: 'discouraged',
    ceoAttestationRequired: true,
    exitInterview: false,
  },
  {
    value: 'Laid_Off',
    label: 'Laid off',
    blurb:
      'No work available — an account was lost or the position was cut. Not about performance.',
    immediate: false,
    rehireDefault: 'Eligible',
    severance: 'expected',
    ceoAttestationRequired: true,
    exitInterview: true,
  },
  {
    value: 'End_Of_Temp_Assignment',
    label: 'End of temp assignment',
    blurb: 'A temporary engagement simply ended. Nobody initiated anything.',
    immediate: false,
    rehireDefault: 'Eligible',
    severance: 'none',
    ceoAttestationRequired: false,
    exitInterview: true,
  },
  {
    value: 'Job_Abandonment',
    label: 'Job abandonment',
    blurb: 'No call, no show, no notice. No exit interview is generated.',
    immediate: true,
    rehireDefault: 'Not eligible',
    severance: 'discouraged',
    ceoAttestationRequired: true,
    exitInterview: false,
  },
  {
    value: 'Client_Conversion',
    label: 'Hired by a client',
    blurb:
      'Hired directly by an account we serve. Within 6 months of their start date the $1,450 conversion fee bills to that client.',
    immediate: false,
    rehireDefault: 'Eligible',
    severance: 'available',
    ceoAttestationRequired: false,
    exitInterview: true,
  },
  {
    value: 'Deceased',
    label: 'Deceased',
    blurb: 'Suppresses all notifications to the employee and removes the exit interview.',
    immediate: true,
    rehireDefault: 'n/a',
    severance: 'none',
    ceoAttestationRequired: false,
    exitInterview: false,
  },
];

export function reasonSpec(v: string | null | undefined): SeparationReasonSpec | undefined {
  return SEPARATION_REASONS.find((r) => r.value === v);
}

export function reasonLabel(v: string | null | undefined): string {
  return reasonSpec(v)?.label ?? (v ? v.replace(/_/g, ' ') : '—');
}

// Position keys an item can be owned by. 'supervisor' has no resolver in the
// system — there is no supervisor/manager/reports-to field on employees and
// positions holds zero rows — so it is asked for once at creation and the
// chosen person is stamped onto the item.
export const OWNER_LABELS: Record<string, string> = {
  coo: 'COO',
  hr: 'HR',
  finance: 'Finance',
  national_am: 'National AM',
  supervisor: 'Supervisor',
};

export const GROUP_LABELS: Record<string, string> = {
  automatic: 'Automatic — the app does these',
  access_and_accounts: 'Access and accounts',
  property_and_subscriptions: 'Company property and subscriptions',
  work_handoff: 'Work handoff',
  employee_file: 'Employee file, pay and benefits',
  accounting: 'Accounting',
};

export const GROUP_ORDER = [
  'automatic',
  'access_and_accounts',
  'property_and_subscriptions',
  'work_handoff',
  'employee_file',
  'accounting',
];

export interface SubStep {
  label?: string | null;
  done?: boolean;
  /** Reassignment proposals use ref/market/proposed_eid/accepted instead. */
  ref?: string;
  market?: string;
  role_on_card?: string;
  proposed_eid?: string | null;
  accepted?: boolean;
}

export interface ChecklistItem {
  item_id: string;
  separation_id: string;
  item_key: string;
  item_group: string;
  label: string;
  detail: string | null;
  sub_steps: SubStep[];
  discovered: string | null;
  owner_position_key: string;
  assigned_to_eid: string | null;
  assigned_by_eid: string | null;
  assignment_note: string | null;
  due_date: string | null;
  governing_date: string;
  is_automatic: boolean;
  is_blocking: boolean;
  auto_rpc: string | null;
  escalated_reason: string | null;
  sort_order: number;
  scheduled_for: string | null;
  completed_at: string | null;
  completed_by_eid: string | null;
  completion_note: string | null;
}

export interface SeparationRecord {
  separation_id: string;
  employee_id: string;
  separation_type: string | null;
  notice_date: string | null;
  last_working_day: string | null;
  reason: string | null;
  performance_trail_complete: string | null;
  eligible_for_rehire: string | null;
  supervisor_eid: string | null;
  final_pay_date: string | null;
  severance_offered: boolean;
  severance_length_days: number | null;
  severance_pay_through: string | null;
  severance_benefits_through: string | null;
  severance_payment_method: string | null;
  severance_release_required: boolean;
  severance_non_solicitation: boolean;
  severance_transition_assistance: boolean;
  severance_notes: string | null;
  ceo_attested: boolean;
  ceo_attested_on: string | null;
  ceo_attestation_note: string | null;
  approved_by_eid: string | null;
  payroll_total: number | null;
  payroll_total_published_at: string | null;
  document_folder_url: string | null;
  conversion_fee: number | null;
  access_revoked_at: string | null;
  status: string | null;
  completed_date: string | null;
  source: string;
  notes: string | null;
}

export interface PickerEmployee {
  employee_id: string;
  display_name: string | null;
  employee_title: string | null;
  work_state: string | null;
}

export interface LooseEnd {
  employee_id: string;
  employee_name: string | null;
  employee_title: string | null;
  separation_date: string | null;
  separation_type: string | null;
  separation_id: string | null;
  facilities_open: number;
  facilities_closed_benign: number;
  route_stops: number;
  vehicles: number;
  active_cards: number;
  active_card_last4: string | null;
  open_tasks: number;
  open_routings: number;
  pipeline_cards: number;
  active_notifications: number;
  record_follows: number;
  open_compensation: number;
  open_benefits: number;
  live_app_access: number;
  person_named_routing: number;
  needs_record: boolean;
}
